import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Shield, FileText, AlertTriangle } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
            <FileText className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Terms and Conditions</h1>
            <p className="text-gray-500">Last updated: December 2024</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Important Notice */}
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold text-red-800 mb-2">IMPORTANT LEGAL NOTICE</h3>
                  <p className="text-red-700 text-sm leading-relaxed">
                    Neon Alpha and its services DO NOT provide investment advice, financial advice, trading advice, 
                    or any other form of professional financial guidance. All information, data, analysis, and tools 
                    provided through this platform are for <strong>ENTERTAINMENT AND INFORMATIONAL PURPOSES ONLY</strong>. 
                    You should always conduct your own due diligence and consult with qualified financial professionals 
                    before making any investment decisions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agreement to Terms */}
          <Card>
            <CardHeader>
              <CardTitle>1. Agreement to Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                By accessing or using Neon Alpha ("the Service," "we," "us," or "our"), you agree to be bound by 
                these Terms and Conditions ("Terms"). If you disagree with any part of these terms, you may not 
                access the Service.
              </p>
              <p>
                These Terms apply to all visitors, users, and others who access or use the Service, including 
                both free and paid subscription tiers.
              </p>
            </CardContent>
          </Card>

          {/* No Investment Advice */}
          <Card>
            <CardHeader>
              <CardTitle>2. No Investment Advice Disclaimer</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p><strong>THE SERVICE DOES NOT PROVIDE INVESTMENT ADVICE.</strong></p>
              <p>
                All content, including but not limited to AI-generated analysis, stock ratings, trade suggestions, 
                analyst consensus data, trending stocks, and any other information provided through the Service:
              </p>
              <ul>
                <li>Is provided for entertainment and informational purposes only</li>
                <li>Should not be construed as investment, financial, legal, or tax advice</li>
                <li>Does not constitute a recommendation to buy, sell, or hold any securities</li>
                <li>Is not tailored to your individual investment objectives, financial situation, or needs</li>
                <li>May contain errors, inaccuracies, or outdated information</li>
                <li>Should not be relied upon as the sole basis for any investment decision</li>
              </ul>
              <p>
                <strong>YOU ARE SOLELY RESPONSIBLE FOR YOUR OWN INVESTMENT DECISIONS.</strong> Always conduct 
                your own research and due diligence, and consult with a qualified financial advisor before 
                making any investment decisions.
              </p>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle>3. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL LONGEVITY AI, ITS OWNERS, 
                OPERATORS, AFFILIATES, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR:
              </p>
              <ul>
                <li>Any direct, indirect, incidental, special, consequential, or punitive damages</li>
                <li>Any loss of profits, revenue, data, or use</li>
                <li>Any trading losses or investment losses</li>
                <li>Any damages arising from your use of or inability to use the Service</li>
                <li>Any damages arising from any content, data, or information obtained through the Service</li>
                <li>Any unauthorized access to or alteration of your account or data</li>
              </ul>
              <p>
                This limitation applies regardless of the legal theory on which the claim is based, including 
                negligence, breach of contract, strict liability, or any other theory.
              </p>
            </CardContent>
          </Card>

          {/* Risk Disclosure */}
          <Card>
            <CardHeader>
              <CardTitle>4. Investment Risk Disclosure</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                <strong>TRADING AND INVESTING INVOLVE SUBSTANTIAL RISK OF LOSS.</strong>
              </p>
              <p>You acknowledge and agree that:</p>
              <ul>
                <li>Past performance is not indicative of future results</li>
                <li>You may lose some or all of your invested capital</li>
                <li>Options, futures, and other derivatives carry additional risks</li>
                <li>Market conditions can change rapidly and unpredictably</li>
                <li>No trading strategy or system can guarantee profits</li>
                <li>AI-generated suggestions may be incorrect or unsuitable for your situation</li>
              </ul>
              <p>
                You should only invest money that you can afford to lose. If you are unsure about any investment, 
                you should seek advice from an independent financial advisor.
              </p>
            </CardContent>
          </Card>

          {/* Subscription Terms */}
          <Card>
            <CardHeader>
              <CardTitle>5. Subscription and Membership</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <h4 className="font-semibold text-gray-900">5.1 Free Tier</h4>
              <p>
                The free tier provides limited access to the Service, including a maximum of 3 stock analyses 
                per day. Free tier users may access basic features but may not access Pro-only features.
              </p>
              
              <h4 className="font-semibold text-gray-900 mt-4">5.2 Pro Access</h4>
              <p>
                Pro access is included with the purchase of THE INCOME BLUEPRINT digital product
                bundle, sold through our official Linktree storefront. By purchasing Pro access:
              </p>
              <ul>
                <li>You agree to pay the applicable one-time bundle price at checkout</li>
                <li>Your account is manually upgraded to Pro within 24 hours of a verified purchase</li>
                <li>You should purchase using the same email address as your platform account</li>
                <li>Refunds are provided at our sole discretion</li>
              </ul>
              
              <h4 className="font-semibold text-gray-900 mt-4">5.3 Pro Features</h4>
              <p>
                Pro subscribers receive access to additional features including unlimited analyses, advanced 
                trade journaling, and premium tools. We reserve the right to modify Pro features at any time.
              </p>
            </CardContent>
          </Card>

          {/* User Responsibilities */}
          <Card>
            <CardHeader>
              <CardTitle>6. User Responsibilities</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>As a user of the Service, you agree to:</p>
              <ul>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Not share your account with others</li>
                <li>Not use the Service for any unlawful purpose</li>
                <li>Not attempt to reverse engineer or disrupt the Service</li>
                <li>Not scrape, copy, or redistribute content without permission</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Intellectual Property */}
          <Card>
            <CardHeader>
              <CardTitle>7. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                The Service and its original content, features, and functionality are owned by Neon Alpha 
                and are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, create derivative works of, publicly display, 
                or exploit any content from the Service without our express written permission.
              </p>
            </CardContent>
          </Card>

          {/* Data Sources */}
          <Card>
            <CardHeader>
              <CardTitle>8. Third-Party Data Sources</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                The Service incorporates data from third-party sources including but not limited to Yahoo Finance, 
                Finnhub, StockAnalysis, and other financial data providers. We do not guarantee the accuracy, 
                completeness, or timeliness of any third-party data.
              </p>
              <p>
                Third-party data is provided "as is" and we disclaim all warranties regarding such data. 
                You should verify any information before relying on it for investment decisions.
              </p>
            </CardContent>
          </Card>

          {/* Modifications */}
          <Card>
            <CardHeader>
              <CardTitle>9. Modifications to Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                We reserve the right to modify or replace these Terms at any time. If a revision is material, 
                we will provide at least 30 days' notice prior to any new terms taking effect. Your continued 
                use of the Service after such modifications constitutes acceptance of the new Terms.
              </p>
            </CardContent>
          </Card>

          {/* Governing Law */}
          <Card>
            <CardHeader>
              <CardTitle>10. Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
                in which the Service operates, without regard to its conflict of law provisions.
              </p>
              <p>
                Any disputes arising from these Terms or your use of the Service shall be resolved through 
                binding arbitration in accordance with applicable arbitration rules.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>11. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                If you have any questions about these Terms, please contact us through the appropriate 
                channels provided on our website.
              </p>
            </CardContent>
          </Card>

          {/* Final Disclaimer */}
          <Card className="border-gray-300 bg-gray-100">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-700 italic">
                By using Neon Alpha, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms and Conditions. You further acknowledge that all information provided is for 
                entertainment purposes only and does not constitute investment advice. You are solely responsible 
                for your own investment decisions and should conduct your own due diligence before making any 
                investment.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
